import {
    Body1,
    Carousel,
    CarouselAnnouncerFunction,
    CarouselCard,
    CarouselNav,
    CarouselNavButton,
    CarouselNavContainer,
    CarouselSlider,
    CarouselViewport,
    makeStyles,
    mergeClasses,
    Tooltip,
} from '@fluentui/react-components';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useIsMobile } from '../../Hooks/useResponsive';
import { useConversations, useGptConfigurations } from '../../store/hooks';

const useClasses = makeStyles({
    slider: {
        paddingLeft: '2rem',
        gap: '3rem',
        marginTop: '4rem',
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'center',
    },
    sliderMobile: {
        gap: '1rem',
        marginTop: '1rem', // Further reduced from 1.5rem
        marginBottom: '0.5rem', // Further reduced from 1rem
        paddingLeft: '0.5rem', // Reduced left padding for consistent peek
        paddingRight: '0.5rem', // Reduced right padding for consistent peek
        display: 'flex',
        alignItems: 'center',
    },
    carousel: {
        marginRight: '2rem',
        marginLeft: '2rem',
        marginBottom: '1rem',
        overflow: 'visible', // Keep this to prevent scrollbars
        // Prevent any scrollbars on the carousel container
        '& .fui-CarouselViewport': {
            overflow: 'visible !important',
        },
        '& .fui-CarouselSlider': {
            overflow: 'visible !important',
        },
    },
    carouselMobile: {
        marginBottom: '0.25rem',
        marginRight: '1rem',
        marginLeft: '1rem',
        overflow: 'visible',
        // Prevent any scrollbars on the carousel container for mobile
        '& .fui-CarouselViewport': {
            overflow: 'visible !important',
        },
        '& .fui-CarouselSlider': {
            overflow: 'visible !important',
        },
        // Ensure cards are properly sized for mobile peek effect
        '& .fui-CarouselCard': {
            flexShrink: 0, // Prevent cards from shrinking
        },
    },
    wireframe: {
        border: '1px #ff5d36 solid',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '0.5rem',
        transition: 'all 0.2s ease-in-out',
        overflow: 'hidden', // Ensure content doesn't overflow the card
        '&:hover': {
            boxShadow: '0 5px 15px rgba(255, 93, 54, 0.3)',
            background: 'rgba(255, 93, 54, 0.05)',
        },
        '&:active': {
            boxShadow: '0 2px 5px rgba(255, 93, 54, 0.2)',
            background: 'rgba(255, 93, 54, 0.1)',
        },
    },
    wireframeMedium: {
        width: '200px',
        padding: '0.5rem 0rem',
    },
    wireframeMediumMobile: {
        width: 'calc(100vw - 60px)', // Adjusted for more consistent peek effect
        maxWidth: '300px', // Keep original max width as fallback
        minWidth: '250px', // Ensure minimum readable size
        padding: '0.25rem 0rem', // Reduced from 0.5rem
    },
    title: {
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        fontSize: '1.4rem',
        fontWeight: '600',
    },
    titleMobile: {
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        fontSize: '1.2rem',
        fontWeight: '600',
    },
});

const WireframeContent: React.FC<{
    appearance: 'odd' | 'even';
    children: React.ReactNode;
    size?: 'small' | 'medium' | 'large';
}> = (props) => {
    const classes = useClasses();
    const isMobile = useIsMobile();

    return <div className={mergeClasses(classes.wireframe, isMobile ? classes.wireframeMediumMobile : classes.wireframeMedium)}>{props.children}</div>;
};

const getAnnouncement: CarouselAnnouncerFunction = (index: number, totalSlides: number, _: number[][]) => {
    return `Carousel slide ${index + 1} of ${totalSlides}`;
};

export class ConversationStartersProps {
    setSelectedConversationStarter: (starter: string) => void = () => {};
}

export const ConversationStarters = ({ setSelectedConversationStarter }: ConversationStartersProps) => {
    const classes = useClasses();
    const { activeGpt } = useConversations();
    const { gptConfigurations } = useGptConfigurations();
    const isMobile = useIsMobile();
    const gptConfiguration = gptConfigurations.find((gpt) => gpt.id === activeGpt?.id);
    return (
        <Carousel 
            draggable 
            announcement={getAnnouncement} 
            className={isMobile ? classes.carouselMobile : classes.carousel} 
            align={isMobile ? 'start' : 'center'}
            // Ensure proper viewport handling
            style={{ overflow: 'visible' }}
        >
            <CarouselViewport style={{ overflow: 'visible' }}>
                <CarouselSlider className={isMobile ? classes.sliderMobile : classes.slider} cardFocus>
                    {gptConfiguration?.conversationStarters?.map((starter, index) => (
                        <Tooltip content={starter.description!} relationship='label' key={index}>
                            <CarouselCard autoSize aria-label={`Carousel Card ${starter.name}`} onClick={() => setSelectedConversationStarter(starter.startingPrompt!)}>
                                <WireframeContent appearance='odd' size='medium'>
                                    <FontAwesomeIcon size='xl' icon={starter.icon! as IconProp} />
                                    <Body1 align='center' className={isMobile ? classes.titleMobile : classes.title}>
                                        {starter.name}
                                    </Body1>
                                </WireframeContent>
                            </CarouselCard>
                        </Tooltip>
                    ))}
                </CarouselSlider>
            </CarouselViewport>

            {gptConfiguration?.conversationStarters && gptConfiguration!.conversationStarters.length > 0 && 
             ((gptConfiguration!.conversationStarters.length > 4) || isMobile) && (
                <CarouselNavContainer layout='inline' next={{ 'aria-label': 'go to next' }} prev={{ 'aria-label': 'go to prev' }}>
                    <CarouselNav>{(index: number) => <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} />}</CarouselNav>
                </CarouselNavContainer>
            )}
        </Carousel>
    );
};
